import {useQuery} from "@apollo/client";
import {useEffect, useMemo, useState} from "react";
import {FiExternalLink} from "react-icons/fi";
import {Link, useLocation, useNavigate} from "react-router";

import {Button} from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {ErrorFallback} from "@/lib/handlers/ErrorFallback";
import {CheckoutContextResult, GQL_CHECKOUT_CONTEXT, GQL_SUBSCRIPTION_PACKS} from "@/lib/queries/billing.ts";
import {AccountType} from "@/lib/types/accounts.ts";
import {BillingPeriod} from "@/lib/types/billing.ts";
import {BillingPeriodPicker} from "@/pages/billing/components/BillingPeriodPicker.tsx";
import {UpsellAlert} from "@/pages/billing/components/UpsellAlert.tsx";
import {Pack} from "@/pages/billing/v2/Pack.tsx";
import {CostBreakdown} from "@/pages/billing/v2/summary/CostBreakdown.tsx";
import {SubscriptionPack, SubscriptionPackTier} from "@/pages/billing/v2/types.ts";

export interface SubscriptionPackTierWrapped extends SubscriptionPackTier {
  packTitle: string;
}
export const Subscriptions = () => {
  const {user} = useCurrentUserContext();
  const location = useLocation();
  const externallySelectedTier = location.state?.selectedTier;
  const {data, error} = useQuery(GQL_SUBSCRIPTION_PACKS, {
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
    errorPolicy: "all",
  });
  const {data: checkoutContextData} = useQuery<CheckoutContextResult>(GQL_CHECKOUT_CONTEXT);
  const packsData: SubscriptionPack[] = useMemo(
    () => data?.subscriptionPacks?.items || [],
    [data?.subscriptionPacks?.items]
  );
  const forcedPacks: SubscriptionPackTierWrapped[] = useMemo(
    () =>
      (packsData || [])
        .filter((p) => p.default_values.is_active || externallySelectedTier?.pack_id === p.pack_id)
        .map((p) => {
          const defaultVariantId =
            externallySelectedTier?.pack_id === p.pack_id ? externallySelectedTier?.tier_id : p.default_values.tier_id;
          const packOption = p.tiers.find((o) => o.tier_id === defaultVariantId);
          return {packTitle: p.title, ...packOption!};
        }),
    [packsData, externallySelectedTier]
  );
  const [bundle, setBundle] = useState<SubscriptionPackTierWrapped[]>([]);
  const hasNoSubscription = !checkoutContextData?.checkoutContext.hasActiveSubscription;
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>(BillingPeriod.YEAR);
  const navigateFn = useNavigate();
  useEffect(() => {
    setBundle(forcedPacks);
    setBillingPeriod(hasNoSubscription ? BillingPeriod.YEAR : user?.account.billing_period || BillingPeriod.YEAR);
  }, [forcedPacks, hasNoSubscription, user]);
  const bundleMessageForSupport = bundle
    .map(
      (tier) =>
        `${tier.packTitle}: ${tier.limits.map((limit) => `${limit.value_alt || limit.value} ${limit.limit_name}`).join(", ")}`
    )
    .join("\n");
  const hasNewItems =
    hasNoSubscription || bundle.filter((t) => !t.is_current).length > 0 || forcedPacks.length != bundle.length;
  const canSubscribe = user?.has_purchase_permission || (hasNoSubscription && user?.has_admin_access);
  if (error) {
    return <ErrorFallback />;
  }
  return (
    <div>
      <div>
        <UpsellAlert
          title={hasNoSubscription ? "Subscribe Today for More Monitoring!" : "Upgrade Today for More Monitoring!"}
        >
          <span>
            {hasNoSubscription ? "Subscribe" : "Upgrade"} to one of the plans available below that best fits your needs.
            Questions about our plans or custom options? Please{" "}
            <Link to={`/contact`} target="_blank">
              Contact Us
            </Link>
            .
          </span>
        </UpsellAlert>
      </div>
      <div className="mt-4">
        <BillingPeriodPicker
          value={billingPeriod}
          onChange={setBillingPeriod}
          disabled={user?.account.billing_period === BillingPeriod.YEAR}
          percentage={20}
        />
      </div>
      <div className="mt-4">
        <div className="row">
          <div className="col-8">
            <div className="row g-4">
              {packsData.map((pack, id) => (
                <div className="col-12 col-lg-6" key={`pack-${id}`}>
                  <Pack
                    title={pack.title}
                    billingPeriod={billingPeriod}
                    default_values={
                      externallySelectedTier?.pack_id === pack.pack_id
                        ? {...pack.default_values, tier_id: externallySelectedTier?.tier_id, is_active: true}
                        : pack.default_values
                    }
                    lines={pack.lines}
                    tiers={pack.tiers}
                    onChange={({value, active}) => {
                      if (!active) {
                        setBundle((prev) => prev.filter((tier) => tier.pack_id != value.pack_id));
                      } else {
                        setBundle((prev) => {
                          const pack = packsData.find((p) => p.pack_id === value.pack_id);
                          const packTitle = pack?.title || "";
                          return prev.filter((tier) => tier.pack_id != value.pack_id).concat([{packTitle, ...value}]);
                        });
                      }
                    }}
                  >
                    {pack.pack_id === "probe_server_locations" && (
                      <div className="pt-2 d-flex align-items-center justify-content-center">
                        <a
                          href="https://uptime.com/probe-server-locations"
                          target="_blank"
                          className="btn border-1 btn-outline-dark pulseTextBtnSmall"
                        >
                          Probe Location Tier Breakdown <FiExternalLink />
                        </a>
                      </div>
                    )}
                  </Pack>
                </div>
              ))}
            </div>
          </div>
          <div className="col-4">
            <CostBreakdown
              title="Subscription Calculator"
              billingPeriod={billingPeriod}
              items={bundle}
              disabled={!hasNoSubscription && !hasNewItems && user?.account.billing_period === billingPeriod}
            >
              <Button
                className="w-100"
                size="large"
                onClick={() => {
                  if (canSubscribe && user.account.type === AccountType.REGULAR) {
                    navigateFn("/billing/checkout", {
                      state: {
                        bundle,
                        billingPeriod,
                      },
                    });
                  } else {
                    navigateFn("/billing/custom-quote", {
                      state: {
                        custom_monitoring_needs: bundleMessageForSupport,
                      },
                    });
                  }
                }}
              >
                {canSubscribe && user.account.type === AccountType.REGULAR
                  ? hasNoSubscription
                    ? "Get Started"
                    : "Update Subscription"
                  : "Contact Support"}
              </Button>
            </CostBreakdown>
          </div>
        </div>
      </div>
    </div>
  );
};
